import React from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-900/95 backdrop-blur-lg border-b border-white/10">
        <div className="w-full px-6 md:px-12 lg:px-20 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold">
              P
            </div>
            <span className="text-xl font-bold text-white">PKF Audit AI</span>
          </div>

          <Link to="/">
            <Button variant="ghost" className="text-white">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Page Content */}
      <div className="w-full px-6 md:px-12 lg:px-20 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-8">Privacy Policy</h1>
          <p className="text-white/60 mb-8">Last updated: May 15, 2025</p>

          <div className="prose prose-invert max-w-none">
            <p className="text-white/80 mb-6">
              PKF Audit AI ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered financial auditing platform ("Service").
            </p>

            <p className="text-white/80 mb-6">
              Please read this Privacy Policy carefully. By accessing or using our Service, you acknowledge that you have read, understood, and agree to be bound by all terms of this Privacy Policy. If you do not agree with our policies and practices, please do not use our Service.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-6">1. Information We Collect</h2>
            <h3 className="text-xl font-semibold mt-6 mb-4">1.1 Personal Information</h3>
            <p className="text-white/80 mb-4">
              We may collect personal information that you voluntarily provide when using our Service, including but not limited to:
            </p>
            <ul className="list-disc pl-6 mb-6 text-white/80">
              <li>Name, email address, and contact information</li>
              <li>Company name and job title</li>
              <li>Account credentials</li>
              <li>Payment information</li>
              <li>Communication preferences</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-4">1.2 Financial and Audit Data</h3>
            <p className="text-white/80 mb-4">
              When you use our Service, you may upload or input financial documents and data for analysis. This may include:
            </p>
            <ul className="list-disc pl-6 mb-6 text-white/80">
              <li>Financial statements</li>
              <li>Audit reports</li>
              <li>Transaction records</li>
              <li>Compliance documentation</li>
              <li>Other financial records</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-4">1.3 Usage Information</h3>
            <p className="text-white/80 mb-4">
              We automatically collect certain information about your device and how you interact with our Service, including:
            </p>
            <ul className="list-disc pl-6 mb-6 text-white/80">
              <li>IP address and device information</li>
              <li>Browser type and settings</li>
              <li>System configuration</li>
              <li>Usage patterns and interaction with features</li>
              <li>Time spent using the Service</li>
            </ul>

            <h2 className="text-2xl font-bold mt-10 mb-6">2. How We Use Your Information</h2>
            <p className="text-white/80 mb-4">
              We use the information we collect for various purposes, including:
            </p>
            <ul className="list-disc pl-6 mb-6 text-white/80">
              <li>Providing, operating, and maintaining our Service</li>
              <li>Processing and analyzing financial documents</li>
              <li>Improving our AI models and algorithms</li>
              <li>Communicating with you about account-related matters</li>
              <li>Processing payments and managing subscriptions</li>
              <li>Detecting and preventing fraud and security incidents</li>
              <li>Complying with legal obligations</li>
              <li>Sending relevant marketing communications (with your consent)</li>
            </ul>

            <h2 className="text-2xl font-bold mt-10 mb-6">3. Data Security</h2>
            <p className="text-white/80 mb-4">
              We implement a variety of security measures to maintain the safety of your personal information and financial data, including:
            </p>
            <ul className="list-disc pl-6 mb-6 text-white/80">
              <li>End-to-end encryption of sensitive data</li>
              <li>Regular security assessments and penetration testing</li>
              <li>Access controls and authentication measures</li>
              <li>Regular security training for our staff</li>
              <li>Compliance with industry security standards</li>
            </ul>
            <p className="text-white/80 mb-6">
              However, no method of transmission or storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-6">4. Data Retention</h2>
            <p className="text-white/80 mb-6">
              We retain your personal information and financial data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. We will securely delete your data when it is no longer needed for these purposes.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-6">5. Sharing Your Information</h2>
            <p className="text-white/80 mb-4">
              We do not sell or rent your personal information or financial data to third parties. We may share your information with:
            </p>
            <ul className="list-disc pl-6 mb-6 text-white/80">
              <li><strong>Service Providers:</strong> Third parties that help us operate, provide, improve, or secure our Service</li>
              <li><strong>Business Partners:</strong> With your explicit consent, we may share information with trusted business partners to offer complementary services</li>
              <li><strong>Legal Requirements:</strong> We may disclose your information when required by law or in response to valid legal requests</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, with appropriate safeguards for your data</li>
            </ul>

            <h2 className="text-2xl font-bold mt-10 mb-6">6. Your Privacy Rights</h2>
            <p className="text-white/80 mb-4">
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc pl-6 mb-6 text-white/80">
              <li>The right to access the personal information we have about you</li>
              <li>The right to request correction of inaccurate information</li>
              <li>The right to request deletion of your information</li>
              <li>The right to restrict or object to processing</li>
              <li>The right to data portability</li>
              <li>The right to withdraw consent</li>
            </ul>
            <p className="text-white/80 mb-6">
              To exercise these rights, please contact us using the information provided in the "Contact Us" section below.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-6">7. International Data Transfers</h2>
            <p className="text-white/80 mb-6">
              Your information may be transferred to, stored, and processed in countries other than your country of residence, including India. We take appropriate measures to ensure that your data is protected and that such transfers comply with applicable data protection laws.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-6">8. Children's Privacy</h2>
            <p className="text-white/80 mb-6">
              Our Service is not intended for children under the age of 16. We do not knowingly collect personal information from children. If we learn that we have collected personal information from a child without parental consent, we will take steps to delete that information.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-6">9. Changes to This Privacy Policy</h2>
            <p className="text-white/80 mb-6">
              We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any material changes by posting the updated Privacy Policy on this page with a new effective date. We encourage you to review this Privacy Policy periodically.
            </p>

            <h2 className="text-2xl font-bold mt-10 mb-6">10. Contact Us</h2>
            <p className="text-white/80 mb-4">
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="text-white/80 mb-8">
              <strong>Email:</strong> support@pkfaudit.ai<br />
              <strong>Address:</strong> PKF Audit AI, Chennai, Tamil Nadu, India
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-white/10 py-8">
        <div className="w-full px-6 md:px-12 lg:px-20 text-center">
          <p className="text-white/60">© {new Date().getFullYear()} PKF Audit AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
